import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import {
  UserX,
  AlertTriangle,
  AlertCircle,
  Ban,
  MailWarning,
  Info,
  Baby,
  MoreHorizontal,
  Flag,
  X,
  CheckCircle2,
  CheckCircle,
} from 'lucide-react';
import { db, auth } from '../firebase';

const ORANGE = '#FF8C08';
const CARD = '#23262C';
const CARD_LIGHT = '#2C2F36';

// Report reasons specific to users (different from listing reports)
const REASONS = [
  { key: 'fake_account', label: 'Fake or Impersonation Account', Icon: UserX },
  { key: 'scam_fraud', label: 'Scam or Fraudulent Behavior', Icon: AlertTriangle },
  { key: 'harassment', label: 'Harassment or Bullying', Icon: AlertCircle },
  { key: 'inappropriate_content', label: 'Inappropriate Profile Content', Icon: Ban },
  { key: 'spam', label: 'Spam or Promotional', Icon: MailWarning },
  { key: 'false_info', label: 'False or Misleading Information', Icon: Info },
  { key: 'underage', label: 'Underage User', Icon: Baby },
  { key: 'other', label: 'Other', Icon: MoreHorizontal },
];

/**
 * ReportUserModal
 *
 * Props:
 * - reportedUserId: string
 * - reportedUserName: string
 * - onClose: function — called to dismiss the modal (equivalent to Navigator.pop)
 */
export default function ReportUserModal({ reportedUserId, reportedUserName, onClose }) {
  const [selectedReason, setSelectedReason] = useState(null);
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState(null);

  const showSnack = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSubmit = async () => {
    if (!selectedReason) return;
    const user = auth.currentUser;
    if (!user) {
      onClose?.();
      return;
    }

    if (user.uid === reportedUserId) {
      showSnack('You cannot report yourself', true);
      return;
    }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'userReports'), {
        reportedUserId,
        reportedUserName,
        reportedBy: user.uid,
        reason: selectedReason,
        details: details.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      setIsSubmitting(false);
    } catch (e) {
      setIsSubmitting(false);
      showSnack('Failed to submit report', true);
    }
  };

  return (
    <div
      style={{
        backgroundColor: CARD,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        maxHeight: '90vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div style={{ padding: '12px 20px 32px' }}>
        {/* Drag handle */}
        <div
          style={{
            width: 40,
            height: 4,
            backgroundColor: 'rgba(255,255,255,0.24)',
            borderRadius: 2,
            margin: '0 auto 20px',
          }}
        />

        {submitted ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'rgba(76,175,80,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircle2 color="#4CAF50" size={36} />
            </div>
            <div style={{ height: 16 }} />
            <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>
              Report Submitted
            </div>
            <div style={{ height: 8 }} />
            <p
              style={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 13,
                lineHeight: 1.5,
                textAlign: 'center',
                margin: 0,
              }}
            >
              Thank you for keeping Filatom safe.
              <br />
              Our team will review this report within 24 hours.
            </p>
            <div style={{ height: 24 }} />
            <button
              onClick={onClose}
              style={{
                width: '100%',
                padding: '14px 0',
                backgroundColor: CARD_LIGHT,
                border: 'none',
                borderRadius: 14,
                color: '#fff',
                fontSize: 15,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  backgroundColor: 'rgba(255,82,82,0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Flag color="#FF5252" size={18} />
              </div>
              <div style={{ width: 12 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#fff', fontSize: 17, fontWeight: 700 }}>
                  Report User
                </div>
                <div
                  style={{
                    color: 'rgba(255,255,255,0.38)',
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Report {reportedUserName}
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: CARD_LIGHT,
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <X color="rgba(255,255,255,0.6)" size={18} />
              </button>
            </div>

            <div style={{ height: 20 }} />
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />
            <div style={{ height: 12 }} />

            {/* Reason list */}
            {REASONS.map((r) => {
              const isSelected = selectedReason === r.key;
              const { Icon } = r;
              return (
                <div
                  key={r.key}
                  onClick={() => setSelectedReason(r.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 8,
                    padding: '13px 14px',
                    borderRadius: 12,
                    cursor: 'pointer',
                    backgroundColor: isSelected ? 'rgba(255,82,82,0.12)' : CARD_LIGHT,
                    border: `1px solid ${isSelected ? 'rgba(255,82,82,0.50)' : 'transparent'}`,
                    transition: 'background-color 150ms, border-color 150ms',
                  }}
                >
                  <Icon color={isSelected ? '#FF5252' : 'rgba(255,255,255,0.38)'} size={18} />
                  <div style={{ width: 12 }} />
                  <div
                    style={{
                      flex: 1,
                      color: isSelected ? '#fff' : 'rgba(255,255,255,0.7)',
                      fontSize: 14,
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    {r.label}
                  </div>
                  {isSelected && <CheckCircle color="#FF5252" size={18} />}
                </div>
              );
            })}

            <div style={{ height: 8 }} />

            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              placeholder="Additional details (optional)…"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: CARD_LIGHT,
                border: 'none',
                borderRadius: 12,
                padding: 14,
                color: '#fff',
                fontSize: 14,
                fontFamily: 'inherit',
                resize: 'none',
                outline: 'none',
              }}
            />

            <div style={{ height: 16 }} />

            <button
              onClick={handleSubmit}
              disabled={selectedReason === null || isSubmitting}
              style={{
                width: '100%',
                height: 50,
                border: 'none',
                borderRadius: 14,
                cursor: selectedReason === null || isSubmitting ? 'default' : 'pointer',
                backgroundColor:
                  selectedReason === null
                    ? 'rgba(255,82,82,0.25)'
                    : '#FF5252',
                color: '#fff',
                fontSize: 15,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isSubmitting ? (
                <span
                  style={{
                    width: 20,
                    height: 20,
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'reportuser-spin 0.8s linear infinite',
                  }}
                />
              ) : (
                'Submit Report'
              )}
            </button>
          </>
        )}
      </div>

      {toast && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 24,
            transform: 'translateX(-50%)',
            backgroundColor: toast.isError ? '#FF5252' : ORANGE,
            color: '#fff',
            padding: '12px 20px',
            borderRadius: 12,
            fontSize: 14,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 1000,
          }}
        >
          {toast.msg}
        </div>
      )}

      <style>{`
        @keyframes reportuser-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export { ReportUserModal };